import { useContext } from 'react';
import { SidebarContext } from 'src/contexts/SidebarContext';

import { alpha, Box, darken, Divider, Drawer, lighten, styled, Theme, useTheme, IconButton } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import SidebarMenu from './SidebarMenu';
import Logo from 'src/components/LogoSign';
import Scrollbar from 'src/components/Scrollbar';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';

const SidebarWrapper = styled(Box)<{ isCollapsed?: boolean }>(
  ({ theme, isCollapsed }) => `
        width: ${isCollapsed ? '80px' : theme.sidebar.width};
        min-width: ${isCollapsed ? '80px' : theme.sidebar.width};
        color: ${theme.colors.alpha.trueWhite[70]};
        position: relative;
        z-index: 7;
        height: 100%;
        transition: width 0.3s ease, min-width 0.3s ease;
`,
);

const CollapseButton = styled(Box)(
  ({ theme }) => `
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        padding: ${theme.spacing(2)};
        display: flex;
        justify-content: center;
        align-items: center;
        border-top: 1px solid ${theme.colors.alpha.trueWhite[10]};
        background: ${alpha(theme.colors.alpha.black[100], 0.2)};
`,
);

function Sidebar() {
  const { sidebarToggle, toggleSidebar, isCollapsed, toggleCollapse } = useContext(SidebarContext);
  const closeSidebar = () => toggleSidebar();
  const theme = useTheme<Theme>();

  return (
    <>
      <SidebarWrapper
        isCollapsed={isCollapsed}
        sx={{
          display: {
            xs: 'none',
            lg: 'inline-block',
          },
          position: 'fixed',
          left: 0,
          top: 0,
          background:
            theme.palette.mode === 'dark'
              ? alpha(lighten(theme.header.background as string, 0.1), 0.5)
              : darken(theme.colors.alpha.black[100], 0.5),
          boxShadow: theme.palette.mode === 'dark' ? theme.sidebar.boxShadow : 'none',
        }}>
        <Scrollbar>
          <Box mt={3} pb={8}>
            <Box
              mx={2}
              sx={{
                width: isCollapsed ? '40px' : '50%',
                transition: 'width 0.3s ease',
              }}>
              <Logo />
            </Box>
          
          <Divider
            sx={{
              mt: theme.spacing(3),
              mx: theme.spacing(2),
              background: theme.colors.alpha.trueWhite[10],
            }}
          />
          <SidebarMenu />
          </Box>
        </Scrollbar>
        <CollapseButton
          sx={{
            background: "none",
            border: "none",
            display: "flex",
            justifyContent: "end",
            alignItems: "center",
          }}
        >
          <IconButton
            onClick={toggleCollapse}
            sx={{
              color: theme.colors.alpha.trueWhite[70],
              '&:hover': {
                color: theme.colors.alpha.trueWhite[100],
                background: alpha(theme.colors.alpha.trueWhite[100], 0.06),
              },
            }}>
            {isCollapsed ? <KeyboardDoubleArrowRightIcon /> : <KeyboardDoubleArrowLeftIcon />}
          </IconButton>
        </CollapseButton>
      </SidebarWrapper>
      <Drawer
        sx={{
          boxShadow: `${theme.sidebar.boxShadow}`,
        }}
        anchor={theme.direction === 'rtl' ? 'right' : 'left'}
        open={sidebarToggle}
        onClose={closeSidebar}
        variant="temporary"
        elevation={9}>
        <SidebarWrapper
          sx={{
            background:
              theme.palette.mode === 'dark'
                ? theme.colors.alpha.white[100]
                : darken(theme.colors.alpha.black[100], 0.5),
          }}>
          <Scrollbar>
            <Box mt={3}>
              <Box
                mx={2}
                sx={{
                  width: 52,
                }}>
                <Logo />
              </Box>
            </Box>
            <Divider
              sx={{
                mt: theme.spacing(3),
                mx: theme.spacing(2),
                background: theme.colors.alpha.trueWhite[10],
              }}
            />
            <SidebarMenu />
          </Scrollbar>
        </SidebarWrapper>
      </Drawer>
    </>
  );
}

export default Sidebar;
